import React, { useEffect, useState, createContext, useContext } from 'react';
import { subscribeToAuth, getCustomerProfile, getCustomerProfileByEmail, signIn as fbSignIn, signUp as fbSignUp, signOutUser, sendPasswordReset, resendVerificationEmail, updateCustomerProfile, updateDisplayName as fbUpdateDisplayName, updateUserEmail as fbUpdateUserEmail, changeUserPassword as fbChangeUserPassword, type CustomerProfile, type User } from '../utils/firebase';
interface AuthContextValue {
  user: User | null;
  profile: CustomerProfile | null;
  loading: boolean;
  /** Why the profile could not be read, when a signed-in user has no profile. */
  profileError: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string, marketingOptIn?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (data: Partial<CustomerProfile>) => Promise<void>;
  updateDisplayName: (name: string) => Promise<void>;
  updateEmail: (newEmail: string, currentPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * A blocked Firestore read can hang instead of rejecting inside sandboxed
 * preview frames, which would pin the whole app in its loading state. Racing
 * the read against a timeout guarantees `loading` always resolves.
 */
const PROFILE_READ_TIMEOUT_MS = 15000;
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([promise, new Promise<T>((_, reject) => {
    setTimeout(() => reject(new Error('Timed out reading your profile. Firestore security rules are most likely blocking reads of your customers document.')), ms);
  })]);
}
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  // Subscribe to auth state changes
  useEffect(() => {
    const unsub = subscribeToAuth(async fbUser => {
      setLoading(true);
      setUser(fbUser);
      if (fbUser) {
        try {
          let resolved = await withTimeout(getCustomerProfile(fbUser.uid), PROFILE_READ_TIMEOUT_MS);
          // The document id should be the Auth UID, but profiles created by
          // hand (or carried over from an earlier account) can be stored under
          // a different id. Fall back to matching on email before giving up.
          if (!resolved && fbUser.email) {
            resolved = await withTimeout(getCustomerProfileByEmail(fbUser.email), PROFILE_READ_TIMEOUT_MS);
          }
          setProfile(resolved);
          setProfileError(resolved ? null : `No customers document was found for this account — not under the ID ${fbUser.uid}, and none with a matching email.`);
        } catch (err) {
          console.error('[Auth] Failed to load profile:', err);
          setProfile(null);
          setProfileError(err instanceof Error ? err.message : 'Failed to load your profile from Firestore.');
        }
      } else {
        setProfile(null);
        setProfileError(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);
  const refreshProfile = async () => {
    if (!user) return;
    const p = await getCustomerProfile(user.uid);
    setProfile(p);
  };
  const value: AuthContextValue = {
    user,
    profile,
    loading,
    profileError,
    signIn: async (email, password) => {
      await fbSignIn(email, password);
    },
    signUp: async (email, password, displayName, marketingOptIn) => {
      await fbSignUp(email, password, displayName, marketingOptIn);
    },
    signOut: async () => {
      await signOutUser();
    },
    resetPassword: async email => {
      await sendPasswordReset(email);
    },
    resendVerification: async () => {
      await resendVerificationEmail();
    },
    refreshProfile,
    updateProfile: async data => {
      if (!user) throw new Error('Not signed in');
      await updateCustomerProfile(user.uid, data);
      await refreshProfile();
    },
    updateDisplayName: async name => {
      await fbUpdateDisplayName(name);
      await refreshProfile();
    },
    updateEmail: async (newEmail, currentPassword) => {
      await fbUpdateUserEmail(newEmail, currentPassword);
      await refreshProfile();
    },
    changePassword: async (currentPassword, newPassword) => {
      await fbChangeUserPassword(currentPassword, newPassword);
    }
  };
  return <AuthContext.Provider value={value} data-id="element-3976" data-mp="3tu4khjqygjfw">{children}</AuthContext.Provider>;
}
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}